import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTheme } from "../../context/ThemeContext";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useUserStore } from "../../store/userStore";
import { canEditHosLimits } from "./bitacora/hos";
import { fetchQuickUnits } from "./quickReportsService";
import { fmtDateTime, userTz } from "./quickReports.compute";
import { EmptyState } from "./QuickReportViews";
import ScheduleEditor from "./scheduled/ScheduleEditor";
import {
  emptySchedule,
  frequencyText,
  isBusy,
  PERIOD_TEXT,
  reportMeta,
  toInput,
  unitsText,
  type ScheduleForm,
  type ScheduledReport,
} from "./scheduled/scheduled";
import { createScheduled, deleteScheduled, listScheduled, runScheduledNow, updateScheduled } from "./scheduled/scheduledService";

const errorText = (err: unknown) =>
  (err as { response?: { data?: { message?: string; errors?: { msg?: string }[] } } })?.response?.data?.errors?.[0]?.msg ||
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
  "Inténtalo de nuevo.";

export default function ScheduledReportsPage() {
  const { isDarkMode } = useTheme();
  const companyId = useCompaniesStore((s) => s.companyId);
  const user = useUserStore((s) => s.user);
  // Crear y editar: la regla de PUT /company, igual que los límites de horas
  // de servicio. Monitorista y web-service ven la lista sin botones.
  const canEdit =
    !!companyId &&
    canEditHosLimits(user?.role_id || localStorage.getItem("userRoleId") || "", user?.company_id || localStorage.getItem("userCompanyId") || "", companyId);
  const tz = useMemo(userTz, []);
  const qc = useQueryClient();
  const [editor, setEditor] = useState<{ id: string | null; initial: ScheduleForm } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const key = ["scheduled-reports", companyId];
  const list = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => listScheduled(companyId!, signal),
    enabled: !!companyId,
    // Mientras un envío está en cola o generándose, se sigue hasta que termine.
    refetchInterval: (q) => ((q.state.data ?? []).some((s) => isBusy(s)) ? 4000 : false),
  });
  const units = useQuery({
    queryKey: ["quick-reports", "units", companyId],
    queryFn: ({ signal }) => fetchQuickUnits(companyId!, signal),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
  const unitNames = useMemo(() => new Map((units.data ?? []).map((u) => [u.id, u.name])), [units.data]);

  const replace = (s: ScheduledReport) => qc.setQueryData<ScheduledReport[]>(key, (prev) => (prev ?? []).map((x) => (x.id === s.id ? { ...x, ...s } : x)));
  const save = useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: ScheduleForm }) => (id ? updateScheduled(companyId!, id, input) : createScheduled(companyId!, input)),
    onSuccess: (_s, { id }) => {
      setEditor(null);
      toast.success(id ? "Programación guardada" : "Informe programado");
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (err) => toast.error(`No se pudo guardar. ${errorText(err)}`),
  });
  const toggle = useMutation({
    mutationFn: (s: ScheduledReport) => updateScheduled(companyId!, s.id, { ...toInput(s), active: !s.active }),
    onSuccess: replace,
    onError: (err) => toast.error(`No se pudo cambiar. ${errorText(err)}`),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteScheduled(companyId!, id),
    onSuccess: (_r, id) => {
      setConfirming(null);
      qc.setQueryData<ScheduledReport[]>(key, (prev) => (prev ?? []).filter((x) => x.id !== id));
      toast.success("Programación borrada");
    },
    onError: (err) => toast.error(`No se pudo borrar. ${errorText(err)}`),
  });
  const runNow = useMutation({
    mutationFn: (id: string) => runScheduledNow(companyId!, id),
    onSuccess: (r: Partial<ScheduledReport> & { id: string }) => {
      replace(r as ScheduledReport);
      toast.info("En camino: llega por correo en unos minutos");
    },
    onError: (err) => toast.error(`No se pudo enviar. ${errorText(err)}`),
  });

  const dark = isDarkMode;
  const muted = dark ? "text-gray-400" : "text-gray-500";
  const strong = dark ? "text-white" : "text-gray-900";
  const items = list.data ?? [];
  const openNew = () => setEditor({ id: null, initial: emptySchedule(tz) });

  const body = () => {
    if (!companyId)
      return <EmptyState isDarkMode={dark} icon="ri-building-2-line" title="Selecciona una compañía" text="Los informes programados son de cada compañía." />;
    if (list.isLoading)
      return (
        <ul className="grid gap-3 xl:grid-cols-2" aria-busy>
          {[0, 1, 2].map((i) => (
            <li key={i} className={`h-44 rounded-2xl border animate-pulse ${dark ? "bg-dark-surface border-dark-border" : "bg-gray-50 border-gray-200"}`} />
          ))}
        </ul>
      );
    if (list.isError)
      return (
        <div className="flex flex-col items-center">
          <EmptyState isDarkMode={dark} icon="ri-error-warning-line" title="No se pudo cargar" text="Revisa tu conexión e inténtalo de nuevo." />
          <button type="button" onClick={() => list.refetch()} className="-mt-8 px-4 py-2 rounded-xl text-sm font-medium bg-primary-500 hover:bg-primary-400 text-white">
            Reintentar
          </button>
        </div>
      );
    if (!items.length)
      return (
        <div className={`rounded-2xl border border-dashed flex flex-col items-center ${dark ? "border-dark-border" : "border-gray-300"}`}>
          <EmptyState
            isDarkMode={dark}
            icon="ri-calendar-schedule-line"
            title="Ningún informe programado"
            text={canEdit ? "Elige un informe rápido o un reporte completo, cada cuándo y a quién: se genera y llega solo por correo." : "Un administrador de la compañía puede programar informes que lleguen solos por correo."}
          />
          {canEdit && (
            <button type="button" onClick={openNew} className="-mt-8 mb-10 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-primary-500 hover:bg-primary-400 text-white transition-[background-color,transform] duration-150 ease-out active:scale-[0.97]">
              <i className="ri-add-line" aria-hidden /> Programar el primero
            </button>
          )}
        </div>
      );
    return (
      <ul className="grid gap-3 xl:grid-cols-2">
        {items.map((s) => (
          <ScheduleCard
            key={s.id}
            s={s}
            units={unitsText(s.device_ids, unitNames)}
            isDarkMode={dark}
            canEdit={canEdit}
            confirming={confirming === s.id}
            busy={{ toggle: toggle.isPending && toggle.variables?.id === s.id, remove: remove.isPending && remove.variables === s.id, run: runNow.isPending && runNow.variables === s.id }}
            onToggle={() => toggle.mutate(s)}
            onEdit={() => setEditor({ id: s.id, initial: toInput(s) })}
            onRun={() => runNow.mutate(s.id)}
            onAskDelete={() => setConfirming(s.id)}
            onCancelDelete={() => setConfirming(null)}
            onDelete={() => remove.mutate(s.id)}
          />
        ))}
      </ul>
    );
  };

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-5 sm:py-8">
        <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <p className={`text-[11px] font-semibold uppercase tracking-wider ${muted}`}>Informes</p>
            <h1 className={`text-2xl font-semibold tracking-tight ${strong}`}>Informes programados</h1>
            <p className={`text-sm max-w-xl ${muted}`}>Un informe rápido o un reporte completo que se genera y llega solo por correo, en Excel o PDF, a la hora que elijas.</p>
          </div>
          {canEdit && items.length > 0 && (
            <button
              type="button"
              onClick={openNew}
              className="self-start sm:self-auto inline-flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-medium bg-primary-500 hover:bg-primary-400 text-white transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              <i className="ri-add-line" aria-hidden /> Nueva programación
            </button>
          )}
        </header>
        {body()}
      </div>
      {editor && (
        <ScheduleEditor
          initial={editor.initial}
          editing={!!editor.id}
          units={units.data ?? []}
          unitsLoading={units.isLoading}
          isDarkMode={dark}
          saving={save.isPending}
          onSave={(input) => save.mutate({ id: editor.id, input })}
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  );
}

type CardProps = {
  s: ScheduledReport;
  units: string;
  isDarkMode: boolean;
  canEdit: boolean;
  confirming: boolean;
  busy: { toggle: boolean; remove: boolean; run: boolean };
  onToggle: () => void;
  onEdit: () => void;
  onRun: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;
};

function LastRun({ s, isDarkMode }: { s: ScheduledReport; isDarkMode: boolean }) {
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  if (isBusy(s))
    return (
      <span className="inline-flex items-center gap-1.5 text-amber-600">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 motion-safe:animate-pulse" aria-hidden />
        {s.last_status === "running" ? "Generando…" : "En cola…"}
      </span>
    );
  if (s.last_status === "ok")
    return (
      <span className="inline-flex items-center gap-1 text-emerald-600">
        <i className="ri-check-line" aria-hidden /> Enviado {fmtDateTime(s.last_run_at, s.tz)}
      </span>
    );
  if (s.last_status === "skipped_empty")
    return (
      <span className={`inline-flex items-center gap-1 min-w-0 ${muted}`} title="Sin datos en el periodo, no se envió">
        <i className="ri-inbox-line flex-shrink-0" aria-hidden />
        <span className="truncate">Sin datos en el periodo, no se envió · {fmtDateTime(s.last_run_at, s.tz)}</span>
      </span>
    );
  if (s.last_status === "error")
    return (
      <span className="inline-flex items-center gap-1 text-red-500 min-w-0" title={s.last_error ?? undefined}>
        <i className="ri-error-warning-line flex-shrink-0" aria-hidden />
        <span className="truncate">Falló {fmtDateTime(s.last_run_at, s.tz)}{s.last_error ? ` · ${s.last_error}` : ""}</span>
      </span>
    );
  return <span className={muted}>Todavía no se envía</span>;
}

function ScheduleCard({ s, units, isDarkMode, canEdit, confirming, busy, onToggle, onEdit, onRun, onAskDelete, onCancelDelete, onDelete }: CardProps) {
  const dark = isDarkMode;
  const muted = dark ? "text-gray-400" : "text-gray-500";
  const strong = dark ? "text-white" : "text-gray-900";
  const meta = reportMeta(s.report, s.kind);
  const ghost = `inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
    dark ? "text-gray-200 hover:bg-white/5" : "text-gray-700 hover:bg-gray-100"
  }`;
  const formats = s.formats.map((f) => (f === "xlsx" ? "Excel" : "PDF")).join(" y ");

  return (
    <li className={`relative rounded-2xl border p-4 sm:p-5 flex flex-col gap-4 transition-opacity duration-200 ${dark ? "bg-dark-card border-dark-border" : "bg-white border-gray-200"} ${s.active ? "" : "opacity-75"}`}>
      <div className="flex items-start gap-3">
        <span className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center ${s.active ? "bg-primary-500/15 text-primary-500" : dark ? "bg-white/5 text-gray-500" : "bg-gray-100 text-gray-400"}`}>
          <i className={`${meta.icon} text-xl`} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className={`text-base font-semibold leading-tight truncate ${strong}`} title={s.name}>
            {s.name}
          </h2>
          <p className={`text-xs mt-0.5 truncate ${muted}`}>
            {meta.name}
            {/* Kilometraje y Paradas existen en los dos catálogos: sin esto no se distinguen. */}
            {s.kind === "full" && <span className={`ml-1.5 rounded px-1 py-px text-[10px] font-semibold uppercase tracking-wider ${dark ? "bg-white/5 text-gray-300" : "bg-gray-100 text-gray-600"}`}>Completo</span>}
            {" "}· {units}
          </p>
        </div>
        {canEdit ? (
          <button
            type="button"
            role="switch"
            aria-checked={s.active}
            aria-label={s.active ? `Pausar ${s.name}` : `Activar ${s.name}`}
            title={s.active ? "Activo: clic para pausar" : "Pausado: clic para activar"}
            disabled={busy.toggle}
            onClick={onToggle}
            className={`relative w-11 h-6 flex-shrink-0 rounded-full transition-colors duration-200 ease-out disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
              s.active ? "bg-primary-500" : dark ? "bg-white/15" : "bg-gray-300"
            }`}
          >
            <span
              aria-hidden
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ease-[cubic-bezier(0.32,0.72,0,1)] ${s.active ? "translate-x-5" : ""}`}
            />
          </button>
        ) : (
          !s.active && <span className={`text-[11px] font-semibold uppercase tracking-wider ${muted}`}>Pausado</span>
        )}
      </div>

      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
        <dt className={muted}>Cuándo</dt>
        <dd className={strong}>
          {frequencyText(s)} <span className={muted}>· incluye {PERIOD_TEXT[s.frequency]}</span>
        </dd>
        <dt className={muted}>Para</dt>
        <dd className={`${strong} truncate`} title={s.recipients.join("\n")}>
          {s.recipients[0]}
          {s.recipients.length > 1 && <span className={muted}> y {s.recipients.length - 1} más</span>}
          <span className={muted}> · {formats}</span>
        </dd>
      </dl>

      <div className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-1 pt-3 border-t text-xs ${dark ? "border-dark-border" : "border-gray-100"}`}>
        <span className={muted}>{s.active && s.next_run_at ? <>Próximo: <span className={`tabular-nums ${strong}`}>{fmtDateTime(s.next_run_at, s.tz)}</span></> : "Pausado: no se envía"}</span>
        <LastRun s={s} isDarkMode={dark} />
      </div>

      {canEdit &&
        (confirming ? (
          <div role="alertdialog" aria-label={`Borrar ${s.name}`} className="flex flex-wrap items-center justify-end gap-2 -mt-1 motion-safe:animate-fadeIn">
            <span className={`text-sm mr-auto ${strong}`}>¿Borrar esta programación?</span>
            <button type="button" onClick={onCancelDelete} className={ghost} autoFocus>
              Cancelar
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={busy.remove}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] disabled:opacity-60"
            >
              {busy.remove && <i className="ri-loader-4-line animate-spin" aria-hidden />} Borrar
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-1 -mx-2 -mt-1">
            <button type="button" onClick={onRun} disabled={busy.run || isBusy(s)} className={ghost} title="Genera el periodo que toca hoy y lo manda a los destinatarios">
              <i className={busy.run ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} aria-hidden /> Enviar ahora
            </button>
            <button type="button" onClick={onEdit} className={ghost}>
              <i className="ri-pencil-line" aria-hidden /> Editar
            </button>
            <button type="button" onClick={onAskDelete} className={`${ghost} ml-auto !text-red-500`} aria-label={`Borrar ${s.name}`}>
              <i className="ri-delete-bin-line" aria-hidden />
            </button>
          </div>
        ))}
    </li>
  );
}
